import format from "string-format";

import { IContentHubClient } from "../clients/content-hub-client";
import { ICultureLoader } from "../clients/culture-loader";
import { IExtendedContentHubClient } from "../clients/extended-client";
import { IEntityDefinition } from "../contracts/base";
import { Entity, IEntity } from "../contracts/base/entity";
import { EntityBase } from "../contracts/base/entity-base";
import { EntityConstructionArgs } from "../contracts/base/entity-construction-args";
import { CultureLoadOption, ICultureLoadOption } from "../contracts/querying/culture-load-option";
import ErrorMessages from "../error-messages";
import { ArgumentError } from "../errors/argument-error";
import Guard from "../guard";
import { LoadOptionsMapper } from "../mappers/load-options-mapper";
import { TypedEntityCreator, TypedEntityCreatorBase } from "../mappers/typed-entity-creator";
import { PropertyFactory } from "./property-factory";
import { IRelationFactory } from "./relation-factory";

/**
 * Factory to create {@link IEntity} instances.
 */
export interface IEntityFactory {
    /**
     * Create an {@link IEntity} instance for the {@link IEntityDefinition} for which a definition name is specified.
     * @param definitionName - Name of the definition from which to create an entity
     * @param cultureLoadOption - Culture load option
     */
    createAsync(definitionName: string, cultureLoadOption?: ICultureLoadOption): Promise<IEntity>;

    /**
     * Create an {@link IEntity} instance for the {@link IEntityDefinition} that is specified.
     * @param definition - The definition from which to create an entity
     * @param cultureLoadOption - Culture load option
     */
    createAsync(definition: IEntityDefinition, cultureLoadOption?: ICultureLoadOption): Promise<IEntity>;
}

export abstract class EntityFactoryBase<T extends EntityBase> implements IEntityFactory {
    private readonly _client: IContentHubClient;
    private readonly _cultureLoader: ICultureLoader;
    private readonly _relationFactory: IRelationFactory;
    private readonly _entityCreator: TypedEntityCreatorBase<T>;

    constructor(
        client: IContentHubClient,
        relationFactory: IRelationFactory,
        cultureLoader: ICultureLoader,
        entityCreator: TypedEntityCreatorBase<T>
    ) {
        Guard.notNullOrUndefined(client);
        Guard.notNullOrUndefined(relationFactory);
        Guard.notNullOrUndefined(cultureLoader);
        Guard.notNullOrUndefined(entityCreator);

        this._client = client;
        this._relationFactory = relationFactory;
        this._cultureLoader = cultureLoader;
        this._entityCreator = entityCreator;
    }

    async createAsync(
        param: string | IEntityDefinition,
        cultureLoadOption: ICultureLoadOption = CultureLoadOption.Default
    ): Promise<IEntity> {
        let definition: IEntityDefinition;
        if (typeof param === "string") {
            Guard.stringNotNullOrEmpty(param);
            definition = await this.getEntityDefinitionAsync(param);
        } else {
            Guard.notNullOrUndefined(param);
            definition = param;
        }

        const cultures = await LoadOptionsMapper.mapCultureLoadOptionsAsync(cultureLoadOption, this._cultureLoader);
        const properties = PropertyFactory.createProperties(definition.getPropertyDefinitions(), cultures);
        const relations = this._relationFactory.createRelations(definition.getRelationDefinitions());

        const args = new EntityConstructionArgs(definition.name, {
            cultures: cultures,
            properties: properties,
            relations: relations,
        });

        const entity = this._entityCreator.createTypedEntity(args);
        entity.startTracking();

        return entity;
    }

    private async getEntityDefinitionAsync(definitionName: string): Promise<IEntityDefinition> {
        const definition = await this._client.entityDefinitions.getCachedAsync(definitionName);

        if (definition == null) {
            const errorMessage = format(ErrorMessages.EntityFactory.DefinitionDoesNotExist, definitionName);
            throw new ArgumentError(errorMessage);
        }

        return definition;
    }
}

export class EntityFactory extends EntityFactoryBase<Entity> {
    constructor(
        client: IExtendedContentHubClient,
        relationFactory: IRelationFactory,
        cultureLoader: ICultureLoader,
        entityCreator: TypedEntityCreator
    ) {
        super(client, relationFactory, cultureLoader, entityCreator);
    }
}
